import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DEMAND, DEMAND_TYPE } from "@demandproai/shared";
import usePfqAxios from "./usePfqAxios";
import Constants from "../Constants";
import { errorToast } from "../utils/toast";
import useSettledSignalEffect from "./useSettledSignalEffect";
import AppConfig from "../AppConfig";
import { useDownloadOptions } from "./useDownloadOptions";

const PREVIEW_UNAVAILABLE_MESSAGE = "Demand document not available.";
const LIFECARE_PLAN_PREVIEW_MESSAGE = "Lifecare plan PDF preview is not available.";
const PREVIEW_OUT_OF_DATE_MESSAGE = "Demand document is being updated. Please try again in a moment.";

const OFFICE_VIEWER_EMBED_BASE = "https://view.officeapps.live.com/op/embed.aspx?src=";
// iis serves its own 404 page instead of the viewer once the query string passes this length
const OFFICE_VIEWER_MAX_QUERY_LENGTH = 2048;
const S3_SECURITY_TOKEN_PARAM = "X-Amz-Security-Token";
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "0.0.0.0"]);

const buildOfficeViewerUrl = (documentUrl) =>
  `${OFFICE_VIEWER_EMBED_BASE}${encodeURIComponent(documentUrl)}`;

// deployed builds set this to a bare hostname while local dev sets a full origin
const toAbsoluteOrigin = (value) => {
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value.replace(/\/$/, "") : `https://${value}`;
};

const isLoopbackOrigin = (origin) => {
  try {
    return LOOPBACK_HOSTS.has(new URL(origin).hostname);
  } catch {
    return true;
  }
};

// the api is published on 443, which the office viewer requires, unlike the pfq base url that carries a port
const previewOrigin = toAbsoluteOrigin(AppConfig.ApiBaseUrl);
// the viewer cannot reach a developer machine, and a local presigned url is short enough to embed directly
const canUsePreviewEndpoint = Boolean(previewOrigin) && !isLoopbackOrigin(previewOrigin);

const buildPreviewEndpointUrl = ({ previewToken }) =>
  `${previewOrigin}${Constants.ApiUrl.docxPreview}?token=${encodeURIComponent(previewToken)}`;

const normalizeWordUrls = (wordUrl) =>
  Array.isArray(wordUrl) ? wordUrl.filter(Boolean) : wordUrl ? [wordUrl] : [];

const isLifecareDemandType = (demandType) =>
  DEMAND_TYPE?.[demandType]?.key === DEMAND_TYPE?.[DEMAND.Lifecare_Plan]?.key;

const isPdfUrl = ({ url }) => /\.pdf(?:$|\?)/i.test(String(url || ""));

const logPreviewDebug = ({ message, data = {} }) => {
  console.log("[DemandStudioPreview]", message, data);
};

const isOverOfficeViewerQueryLimit = ({ queryLength }) => queryLength > OFFICE_VIEWER_MAX_QUERY_LENGTH;

const getUrlHost = ({ url }) => {
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
};

const getSecurityTokenLength = ({ documentUrl }) => {
  try {
    return new URL(documentUrl).searchParams.get(S3_SECURITY_TOKEN_PARAM)?.length ?? 0;
  } catch {
    return 0;
  }
};

const logOfficeViewerDiagnostics = ({ documentUrl, viewerUrl }) => {
  const queryLength = viewerUrl.length - OFFICE_VIEWER_EMBED_BASE.indexOf("?") - 1;
  const securityTokenLength = getSecurityTokenLength({ documentUrl });
  const isOverLimit = isOverOfficeViewerQueryLimit({ queryLength });
  const isPreviewEndpoint = documentUrl.startsWith(previewOrigin) && Boolean(previewOrigin);
  logPreviewDebug({
    message: isOverLimit
      ? "Office viewer url exceeds the query length limit and will render an IIS 404"
      : "Office viewer url built",
    data: {
      documentUrlLength: documentUrl.length,
      viewerUrlLength: viewerUrl.length,
      queryLength,
      queryLimit: OFFICE_VIEWER_MAX_QUERY_LENGTH,
      overBy: isOverLimit ? queryLength - OFFICE_VIEWER_MAX_QUERY_LENGTH : 0,
      hasSecurityToken: securityTokenLength > 0,
      securityTokenLength,
      isPreviewEndpoint,
      documentHost: getUrlHost({ url: documentUrl })
    }
  });
};

const resolveDocumentUrl = ({ targetUrl, previewToken, shouldUsePreviewEndpoint }) =>
  shouldUsePreviewEndpoint && previewToken ? buildPreviewEndpointUrl({ previewToken }) : targetUrl;

const buildViewerUrl = ({ targetUrl, previewToken, shouldUsePreviewEndpoint, demandType }) => {
  if (!targetUrl) return "";
  const shouldRenderPdf = isLifecareDemandType(demandType) || isPdfUrl({ url: targetUrl });
  if (shouldRenderPdf) {
    logPreviewDebug({
      message: "Rendering pdf directly",
      data: { documentUrlLength: targetUrl.length, documentHost: getUrlHost({ url: targetUrl }) }
    });
    return targetUrl;
  }
  const documentUrl = resolveDocumentUrl({ targetUrl, previewToken, shouldUsePreviewEndpoint });
  const viewerUrl = buildOfficeViewerUrl(documentUrl);
  logOfficeViewerDiagnostics({ documentUrl, viewerUrl });
  return viewerUrl;
};

const getPreviewLabel = ({ url, index }) => {
  try {
    const pathName = new URL(url).pathname;
    const fileName = decodeURIComponent(pathName.split("/").pop() || "");
    const cleanName = fileName.replace(/\.docx$/i, "");
    return cleanName || `Document ${index + 1}`;
  } catch {
    return `Document ${index + 1}`;
  }
};

const getDemandTypeLabel = ({ demandValue }) => {
  const demandType = DEMAND_TYPE?.[demandValue];
  if (demandType?.text) return demandType.text;
  return String(demandValue || "").replace(/_/g, " ");
};

const getDemandOptionsFromDocxFiles = ({ docxFiles }) => {
  if (!docxFiles || typeof docxFiles !== "object") return [];
  return Object.keys(DEMAND_TYPE || {}).reduce((acc, demandValue) => {
    const key = DEMAND_TYPE?.[demandValue]?.key;
    if (!key || !docxFiles?.[key]?.s3UrlPath) return acc;
    acc.push({
      value: demandValue,
      text: getDemandTypeLabel({ demandValue })
    });
    return acc;
  }, []);
};

const getClientDisplayName = ({ client, index }) =>
  client?.personalInfo?.clientName || `Plaintiff ${index + 1}`;

const getLifecareClientOptions = ({ clients }) => {
  if (!Array.isArray(clients)) {
    return [];
  }
  return clients.reduce((acc, client, index) => {
    const hasLifecarePlan = Boolean(client?.result?.lifecarePlan?.s3FilePath);
    if (!hasLifecarePlan || !client?.clientId) {
      return acc;
    }
    acc.push({
      value: client.clientId,
      text: getClientDisplayName({ client, index })
    });
    return acc;
  }, []);
};

const hasLifecareClients = ({ clients }) => getLifecareClientOptions({ clients }).length > 0;

const getHasLifecarePlanForClient = ({ clients, clientId }) => {
  const client = Array.isArray(clients)
    ? clients.find((item) => item?.clientId === clientId)
    : null;
  return Boolean(client?.result?.lifecarePlan?.s3FilePath);
};

const addLifecareDemandOption = ({ options }) => {
  const lifecareValue = DEMAND.Lifecare_Plan;
  const hasLifecareOption = options.some((option) => option.value === lifecareValue);
  if (hasLifecareOption) {
    return options;
  }
  return [
    ...options,
    {
      value: lifecareValue,
      text: getDemandTypeLabel({ demandValue: lifecareValue })
    }
  ];
};

const getDemandOptions = ({ docxFiles, clients }) => {
  const options = getDemandOptionsFromDocxFiles({ docxFiles });
  if (!hasLifecareClients({ clients })) {
    return options;
  }
  return addLifecareDemandOption({ options });
};

const useDemandStudioPreview = ({
  caseId,
  isOpen,
  docxFiles,
  clients,
  forcedDemandType,
  forcedClientId,
  onStaleDocuments,
  suggestedEditsSettledSignal
}) => {
  const { postData, isLoading } = usePfqAxios();
  const { downloadDemandDraft } = useDownloadOptions();
  const [isSelfHealing, setIsSelfHealing] = useState(false);
  const [viewerUrl, setViewerUrl] = useState("");
  const [error, setError] = useState("");
  const [docxOptions, setDocxOptions] = useState([]);
  const [selectedDocxUrl, setSelectedDocxUrl] = useState("");
  const [selectedDemandType, setSelectedDemandType] = useState(forcedDemandType || DEMAND.TP_PLD);
  const [demandOptions, setDemandOptions] = useState([]);
  const [lifecareClientOptions, setLifecareClientOptions] = useState([]);
  const [selectedLifecareClientId, setSelectedLifecareClientId] = useState("");
  const [currentPreviewToken, setCurrentPreviewToken] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const selectedDocxUrlRef = useRef("");
  const fetchPreviewRef = useRef(null);
  // one regeneration per stale preview, so a case that stays dirty cannot spin the loop
  const hasAttemptedSelfHealRef = useRef(false);

  const setUnavailablePreview = useCallback(({ message = PREVIEW_UNAVAILABLE_MESSAGE, shouldToast = false } = {}) => {
    logPreviewDebug({ message: "Preview unavailable", data: { message, shouldToast } });
    setViewerUrl("");
    setError(message);
    setDocxOptions([]);
    setSelectedDocxUrl("");
    setCurrentPreviewToken("");
    if (shouldToast) {
      errorToast(message);
    }
  }, []);

  const applyPreviewUrl = useCallback(({ targetUrl, previewToken, shouldUsePreviewEndpoint, demandType }) => {
    const nextViewerUrl = buildViewerUrl({ targetUrl, previewToken, shouldUsePreviewEndpoint, demandType });
    setViewerUrl(nextViewerUrl);
  }, []);

  const selectDocx = useCallback(
    ({ url, previewToken, shouldUsePreviewEndpoint, demandType }) => {
      logPreviewDebug({
        message: "Selecting document",
        data: {
          hasUrl: Boolean(url),
          hasPreviewToken: Boolean(previewToken),
          shouldUsePreviewEndpoint,
          demandType
        }
      });
      selectedDocxUrlRef.current = url || "";
      setSelectedDocxUrl(url || "");
      if (!url) {
        setViewerUrl("");
        return;
      }
      applyPreviewUrl({ targetUrl: url, previewToken, shouldUsePreviewEndpoint, demandType });
    },
    [applyPreviewUrl]
  );

  const isLifecareSelected = useMemo(() => {
    return isLifecareDemandType(selectedDemandType);
  }, [selectedDemandType]);

  const isPreviewUnavailiable = useMemo(() => {
    // skip the generate round-trip (and the in-iframe backend error) when the client
    // has no rendered plan yet; the body shows a friendly empty state instead
    const hasLifecarePlanForClient = getHasLifecarePlanForClient({ clients, clientId: selectedLifecareClientId });
    console.log("isLifecareSelected: ", isLifecareSelected);
    console.log("selectedLifecareClientId: ", selectedLifecareClientId);
    console.log("hasLifecarePlanForClient: ", hasLifecarePlanForClient);
    const shouldBlockPreview = isLifecareSelected && (!selectedLifecareClientId || !hasLifecarePlanForClient);
    if (shouldBlockPreview) {
      setUnavailablePreview({ message: "Please select a client with a lifecare plan." });
    }
    return Boolean(shouldBlockPreview);
  }, [setUnavailablePreview, selectedLifecareClientId, clients, isLifecareSelected]);

  const fetchPreview = useCallback(async () => {
    if (!caseId) return;
    logPreviewDebug({ message: "Fetching preview", data: { caseId } });
    setError("");
    if (isPreviewUnavailiable) {
      return;
    }

    const requestPayload = {
      caseId,
      typeOfDemandDraft: selectedDemandType
    };
    if (isLifecareSelected) {
      requestPayload.clientId = selectedLifecareClientId;
      requestPayload.dispositionType = "inline";
    }
    const response = await postData(Constants.ApiUrl.pfqDownload.generate, {
      ...requestPayload
    });

    logPreviewDebug({
      message: "Generate response received",
      data: {
        success: Boolean(response?.success),
        hasData: Boolean(response?.data),
        selectedDemandType,
        isUpToDate: response?.data?.isUpToDate,
        wordUrlType: Array.isArray(response?.data?.wordUrl) ? "array" : typeof response?.data?.wordUrl
      }
    });

    if (!response?.success) {
      setUnavailablePreview({
        message: response?.message || PREVIEW_UNAVAILABLE_MESSAGE,
        shouldToast: true,
      });
      return;
    }

    const { wordUrl, previewToken, isUpToDate } = response?.data ?? {};

    // an edit that landed mid regeneration left the documents dirty, so let the owner rebuild them before showing anything stale
    if (isUpToDate === false) {
      if (hasAttemptedSelfHealRef.current || !onStaleDocuments) {
        setUnavailablePreview({ message: PREVIEW_OUT_OF_DATE_MESSAGE, shouldToast: true });
        return;
      }
      hasAttemptedSelfHealRef.current = true;
      logPreviewDebug({ message: "Documents are stale, regenerating before preview" });
      setIsSelfHealing(true);
      try {
        await onStaleDocuments();
      } catch (regenerationError) {
        logPreviewDebug({ message: "Regeneration failed", data: { error: regenerationError?.message } });
        setUnavailablePreview({ message: PREVIEW_OUT_OF_DATE_MESSAGE, shouldToast: true });
        return;
      } finally {
        setIsSelfHealing(false);
      }
      return fetchPreviewRef.current?.();
    }

    const urls = normalizeWordUrls(wordUrl);
    // one token identifies one demand type, so a multi document response has to fall back to the presigned urls
    const shouldUsePreviewEndpoint = canUsePreviewEndpoint && urls.length === 1 && !isLifecareSelected;
    const options = urls.map((url, index) => ({
      value: url,
      text: getPreviewLabel({ url, index })
    }));
    const nextSelectedUrl = urls.includes(selectedDocxUrlRef.current) ? selectedDocxUrlRef.current : urls[0];
    setCurrentPreviewToken(shouldUsePreviewEndpoint ? previewToken || "" : "");

    logPreviewDebug({
      message: "Resolved preview options",
      data: {
        urlCount: urls.length,
        dropdownVisible: options.length > 1,
        selectedFromExisting: urls.includes(selectedDocxUrlRef.current)
      }
    });

    if (!nextSelectedUrl) {
      const unavailableMessage = isLifecareSelected
        ? LIFECARE_PLAN_PREVIEW_MESSAGE
        : PREVIEW_UNAVAILABLE_MESSAGE;
      setUnavailablePreview({ message: unavailableMessage });
      return;
    }

    setDocxOptions(options);
    selectDocx({
      url: nextSelectedUrl,
      previewToken,
      shouldUsePreviewEndpoint,
      demandType: selectedDemandType
    });
  }, [
    caseId,
    postData,
    selectedDemandType,
    selectedLifecareClientId,
    clients,
    selectDocx,
    setUnavailablePreview,
    isLifecareSelected,
    onStaleDocuments
  ]);
  fetchPreviewRef.current = fetchPreview;

  const handleSelectDocx = useCallback(({ value }) => {
    logPreviewDebug({ message: "Dropdown changed", data: { hasValue: Boolean(value) } });
    selectedDocxUrlRef.current = value || "";
    setSelectedDocxUrl(value || "");
    if (!value) {
      setViewerUrl("");
      return;
    }
    setViewerUrl(buildViewerUrl({
      targetUrl: value,
      previewToken: currentPreviewToken,
      shouldUsePreviewEndpoint: Boolean(currentPreviewToken),
      demandType: selectedDemandType
    }));
  }, [currentPreviewToken, selectedDemandType]);

  const handleSelectDemandType = useCallback(({ value }) => {
    logPreviewDebug({ message: "Demand type changed", data: { value } });
    setSelectedDemandType(value || DEMAND.TP_PLD);
    setDocxOptions([]);
    hasAttemptedSelfHealRef.current = false;
    selectedDocxUrlRef.current = "";
    setSelectedDocxUrl("");
    setViewerUrl("");
    setCurrentPreviewToken("");
  }, []);

  const handleSelectLifecareClient = useCallback(({ value }) => {
    setSelectedLifecareClientId(value || "");
    setDocxOptions([]);
    hasAttemptedSelfHealRef.current = false;
    selectedDocxUrlRef.current = "";
    setSelectedDocxUrl("");
    setViewerUrl("");
    setError("");
    setCurrentPreviewToken("");
  }, []);

  useEffect(() => {
    if (forcedDemandType) {
      setDemandOptions([]);
      setSelectedDemandType(forcedDemandType);
      return;
    }
    const options = getDemandOptions({ docxFiles, clients });
    setDemandOptions(options);
    if (!options.length) {
      setSelectedDemandType(DEMAND.TP_PLD);
      return;
    }
    const hasSelected = options.some((option) => option.value === selectedDemandType);
    if (hasSelected) return;
    const tpPldOption = options.find((option) => option.value === DEMAND.TP_PLD);
    setSelectedDemandType(tpPldOption?.value || options[0]?.value || DEMAND.TP_PLD);
  }, [docxFiles, clients, selectedDemandType, forcedDemandType]);

  useEffect(() => {
    const options = getLifecareClientOptions({ clients });
    setLifecareClientOptions(options);
    // lifecare studio drives the client externally and is the source of truth; the
    // generate endpoint resolves the PDF from the clientId, so follow it even when the
    // client is not in the s3FilePath-derived options. Route through the select handler
    // so the stale PDF is cleared immediately and the loading state shows on switch
    if (forcedClientId) {
      if (forcedClientId !== selectedLifecareClientId) {
        handleSelectLifecareClient({ value: forcedClientId });
      }
      return;
    }
    if (!options.length) {
      setSelectedLifecareClientId("");
      return;
    }
    const hasSelected = options.some((option) => option.value === selectedLifecareClientId);
    if (hasSelected) {
      return;
    }
    setSelectedLifecareClientId(options[0].value);
  }, [clients, selectedLifecareClientId, forcedClientId, handleSelectLifecareClient]);

  useEffect(() => {
    if (!isOpen) {
      hasAttemptedSelfHealRef.current = false;
      return;
    }
    fetchPreview();
  }, [fetchPreview, isOpen]);

  useSettledSignalEffect({
    signal: suggestedEditsSettledSignal,
    caseId,
    isEnabled: isOpen,
    onSettled: fetchPreview,
  });

  const handleDownloadPreview = useCallback(async () => {
    if (!caseId || !selectedDemandType) return;
    if (isLifecareSelected && !selectedLifecareClientId) return;
    setIsDownloading(true);
    try {
      await downloadDemandDraft({
        caseId,
        typeOfDemandDraft: selectedDemandType,
        clientId: isLifecareSelected ? selectedLifecareClientId : undefined
      });
    } finally {
      setIsDownloading(false);
    }
  }, [caseId, selectedDemandType, selectedLifecareClientId, downloadDemandDraft]);

  return {
    viewerUrl,
    // a self healing regeneration keeps the pane in its loading state instead of flashing an error
    isLoading: isLoading || isSelfHealing,
    error,
    demandOptions,
    selectedDemandType,
    onSelectDemandType: handleSelectDemandType,
    lifecareClientOptions,
    selectedLifecareClientId,
    onSelectLifecareClient: handleSelectLifecareClient,
    docxOptions,
    selectedDocxUrl,
    onSelectDocx: handleSelectDocx,
    refreshPreview: fetchPreview,
    onDownloadPreview: handleDownloadPreview,
    isDownloading,
    canDownloadPreview: !isPreviewUnavailiable
  };
};

export default useDemandStudioPreview;
